import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Col, Row } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';

import appConfig from '@config/app';
import {
  getGuestReferCatalogRequest,
  getGuestReferLinkRequest,
  submitGuestReferralRequest,
  validateGuestReferStepRequest,
} from '@reducers/guest/GuestAction';
import { clearReferralDataDispatch, markAsUrgentDispatch } from '@reducers/referral/ReferralAction';
import {
  createPatientHistoryAction,
  createPatientInformationAction,
  saveReferralReasonAction,
  showReferralAction,
} from '@reducers/referral/ReferralActionConfig';
import { handleError } from '@services/ErrorHandler';
import LocaleService from '@services/LocaleService';
import Checkbox from '@shared/components/input/Checkbox';
import { formatPhoneNumber, getInitials } from '@shared/helpers';
import { EmailIcon, PhoneIcon, UrgentFormFlagIcon } from '@shared/icons';
import AttachmentForm from '@shared/layout/dashboard/components/send-referral-order/components/AttachmentForm';
import PatientHistoryForm from '@shared/layout/dashboard/components/send-referral-order/components/PatientHistoryForm';
import PatientInformationForm from '@shared/layout/dashboard/components/send-referral-order/components/PatientInformationForm';
import ReferralReasonForm from '@shared/layout/dashboard/components/send-referral-order/components/ReasonForReferralForm';
import {
  GuestReferral,
  GuestReferralProvider,
  StepRequests,
} from '@shared/layout/dashboard/components/send-referral-order/guest/GuestReferralContext';
import { LoadingBlock } from '@shared/utils';

import GuestPreferredDentist, { GuestDentist } from './components/GuestPreferredDentist';
import GuestReferralSubmitted from './components/GuestReferralSubmitted';
import GuestSenderForm, { GuestSender } from './components/GuestSenderForm';
import useGuestCaptcha from './components/useGuestCaptcha';
import LinkStateScreen from '../referral/components/LinkStateScreen';

import '@shared/layout/dashboard/components/send-referral-order/SendReferralOrderPageStyle.scss';
import '../referral/GuestReferralStyle.scss';
import './GuestReferStyle.scss';

const i18n = LocaleService.getTranslations('referrals.guestRefer') as any;
const i18nSend = LocaleService.getTranslations('referrals.sendReferral') as any;

type ClinicSummary = {
  id: string;
  name: string;
  email?: string;
  phone_number?: string | null;
  address?: string | null;
  image_url?: string | null;
};

type LinkProblem = 'link_invalid' | 'link_inactive';

// What each step's block holds in the submission (the product steps' own keys).
const PATIENT_FIELDS = [
  'first_name', 'last_name', 'birthdate', 'email', 'mobile_number',
  'street', 'city', 'province', 'postal_code', 'gps_location',
];

const STEP_OF_BLOCK: Record<string, string> = {
  sender: '1', patient: '2', history: '3', reason: '4', attachments: '5',
};

type ClinicContact = { email?: string; phone_number?: string | null };

/** The screen's wording; a switched-off link names how to reach the clinic instead. */
const linkStateCopy = (state: LinkProblem, contact: ClinicContact | null) => {
  const copy = i18n.linkStates[state];
  if (state !== 'link_inactive') return copy;

  const phone = contact?.phone_number ? formatPhoneNumber(contact.phone_number) : '';
  const email = contact?.email ?? '';
  const reach = phone && email
    ? LocaleService.parseTranslation(copy.contactOr, { ':phone': phone, ':email': email })
    : phone || email;

  return {
    title: copy.title,
    message: reach ? LocaleService.parseTranslation(copy.message, { ':contact': reach }) : copy.messageNoContact,
  };
};

/** Resolves like a request creator's $promise; the step forms read `result.data.data`. */
const asRequest = (run: () => Promise<any>) => () => ({ $promise: run() });

/**
 * ENH-052 — a clinic's public referral form, `/refer/:slug`, for a dentist
 * without an account.
 *
 * Steps 2–5 are the signed-in form's own components (plan D11): under a
 * GuestReferralProvider they keep the referral in the browser, with each
 * step checked on Next by the server's submission rules, and the last step
 * submits it all at once. Step 1 asks who the guest is; the recipient is the
 * link's clinic and cannot be changed, though the guest may pick one of its
 * dentists (ENH-056).
 */
const GuestReferPage = () => {
  const dispatch = useDispatch<any>();
  const { slug = '' } = useParams();
  const isUrgent = useSelector(({ Referral }) => Referral.isUrgent);
  const [ loading, setLoading ] = useState(true);
  const [ linkProblem, setLinkProblem ] = useState<LinkProblem | null>(null);
  // How to reach the clinic when it has turned its link off (sent with link_inactive).
  const [ clinicContact, setClinicContact ] = useState<ClinicContact | null>(null);
  const [ clinic, setClinic ] = useState<ClinicSummary | null>(null);
  const [ dentists, setDentists ] = useState<GuestDentist[]>([]);
  const [ dentistId, setDentistId ] = useState<string | null>(null);
  const [ dentistError, setDentistError ] = useState('');
  const [ captchaRequired, setCaptchaRequired ] = useState(false);
  const [ open, setOpen ] = useState('1');
  const [ sender, setSender ] = useState<GuestSender | null>(null);
  const [ hasAccount, setHasAccount ] = useState(false);
  const [ consent, setConsent ] = useState(false);
  const [ consentError, setConsentError ] = useState('');
  const [ captchaError, setCaptchaError ] = useState('');
  const [ submitted, setSubmitted ] = useState<any>(null);
  const catalogRef = useRef<any>({});
  const draftRef = useRef<any>(null);
  const senderRef = useRef<GuestSender | null>(null);
  const dentistIdRef = useRef<string | null>(null);
  // One per form: sending it again (double click, retry) returns the first result.
  const submissionKey = useRef(uuidv4());
  const captcha = useGuestCaptcha(captchaRequired);
  // Read through a ref so the stand-ins below keep one identity for the page's life.
  const captchaRef = useRef(captcha);
  captchaRef.current = captcha;

  const toggle = useCallback((id: string) => {
    setOpen((current) => (current === id ? '' : id));
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [ link, catalog ] = await Promise.all([
          dispatch(getGuestReferLinkRequest(slug)).$promise,
          dispatch(getGuestReferCatalogRequest(slug)).$promise,
        ]);
        const linkClinic: ClinicSummary = link?.data?.data?.clinic;
        catalogRef.current = catalog?.data?.data ?? {};
        setCaptchaRequired(!!link?.data?.data?.captcha_required);
        setClinic(linkClinic);
        setDentists(link?.data?.data?.dentists ?? []);

        // The draft has the shape the steps read from a saved referral; the
        // synthetic ids pass their "is this step saved yet" checks.
        draftRef.current = {
          id: 'guest',
          type: 'referral',
          status: 'draft',
          is_urgent: false,
          referred_to: { id: linkClinic.id, type: 'dental-clinic', name: linkClinic.name },
          tooth: [],
          remarks: '',
          services: [],
          attachments: [],
        };
        dispatch(clearReferralDataDispatch());
        dispatch(showReferralAction({ data: draftRef.current }));
        dispatch(markAsUrgentDispatch(false));
      } catch (error: any) {
        const code = error?.response?.data?.error_code;
        setClinicContact(error?.response?.data?.contact ?? null);
        setLinkProblem(code === 'link_inactive' ? 'link_inactive' : 'link_invalid');
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ slug ]);

  const pickDentist = useCallback((id: string | null) => {
    dentistIdRef.current = id;
    setDentistId(id);
    setDentistError('');
  }, []);

  // Leaving the page must not leave a guest's draft in the store.
  useEffect(() => () => {
    dispatch(clearReferralDataDispatch());
  }, [ dispatch ]);

  /** A refusal that is not about a field: the link, the limits, the network. */
  const showProblem = useCallback((error: any) => {
    const data = error?.response?.data;
    const code = data?.error_code;
    if (code === 'link_invalid' || code === 'link_inactive') {
      setClinicContact(data?.contact ?? null);
      setLinkProblem(code);
      return;
    }
    if (code === 'too_many_requests') {
      toast.error(LocaleService.parseTranslation(i18n.errors.tooManyRequests, {
        ':minutes': Math.max(1, Math.ceil((Number(data?.retry_after) || 60) / 60)),
      }));
      return;
    }
    handleError(error);
  }, []);

  /** Field errors (422) go back to the step, which shows them; anything else is shown here. */
  const checkStep = useCallback(async (step: string, data: any) => {
    try {
      await dispatch(validateGuestReferStepRequest(slug, step, data)).$promise;
    } catch (error: any) {
      if (error?.response?.status === 422) throw error;
      showProblem(error);
      // No response: the step has nothing more to show.
      throw new Error('handled');
    }
  }, [ dispatch, slug, showProblem ]);

  const keep = useCallback((action: any, draft: any) => {
    draftRef.current = draft;
    dispatch(action({ data: draft }));
    return { data: { data: draft } };
  }, [ dispatch ]);

  const submit = useCallback(async (data: any) => {
    const draft = draftRef.current;
    const patient = draft.patient ?? {};
    const record = patient.medical_record ?? {};

    const body = {
      sender: senderRef.current,
      patient: {
        ...PATIENT_FIELDS.reduce((acc: any, key) => ({
          ...acc, [key]: patient.address?.[key] ?? patient[key] ?? '',
        }), {}),
        // Typed without the country code, as in the signed-in form.
        mobile_number: String(patient.mobile_number ?? '').replace(/^\+1/, ''),
      },
      history: {
        medical_history: record.medical_history ?? '',
        allergies: record.allergies ?? [],
        special_considerations: record.special_considerations ?? [],
        need_sedations: record.need_sedations ?? [],
      },
      reason: {
        tooth: draft.tooth ?? [],
        remarks: draft.remarks ?? '',
        new_referral_services: (draft.services ?? []).map((service: any) => ({
          specialties_id: service.specialties_id,
          specialties_name: service.specialties,
          specialties_parent_id: service.parent_id ?? null,
          remarks: service.remarks ?? null,
        })),
      },
      attachments: {
        files: (data.files ?? []).map((file: any) => ({
          file: file.file,
          filename: file.filename,
          file_size: `${file.file_size}`,
        })),
      },
      is_urgent: !!data.is_urgent,
      consent: true,
      recaptcha_token: await captchaRef.current.getToken(),
      submission_key: submissionKey.current,
      // Left out for "Any available dentist": the clinic itself, as before.
      ...(dentistIdRef.current ? { preferred_dentist_id: dentistIdRef.current } : {}),
    };

    return dispatch(submitGuestReferralRequest(slug, body)).$promise;
  }, [ dispatch, slug ]);

  // Stand-ins for the steps' request creators (plan D11): the same arguments
  // and the same `$promise`, but the referral stays here until it is submitted.
  const requests: StepRequests = useMemo(() => {
    const savePatient = (data: any) => asRequest(async () => {
      const fields = PATIENT_FIELDS.reduce((acc: any, key) => ({ ...acc, [key]: data[key] ?? '' }), {});
      await checkStep('patient', fields);
      const draft = draftRef.current;
      return keep(createPatientInformationAction, {
        ...draft,
        is_urgent: !!data.is_urgent,
        patient: {
          ...draft.patient,
          id: 'guest',
          first_name: fields.first_name,
          last_name: fields.last_name,
          full_name: `${fields.first_name} ${fields.last_name}`,
          birthdate: fields.birthdate,
          email: fields.email,
          mobile_number: `+1${fields.mobile_number}`,
          should_notify: false,
          address: {
            street: fields.street,
            city: fields.city,
            province: fields.province,
            postal_code: fields.postal_code,
            gps_location: fields.gps_location,
          },
        },
      });
    });

    const saveHistory = (data: any) => asRequest(async () => {
      const fields = {
        medical_history: data.medical_history ?? '',
        allergies: data.allergies ?? [],
        special_considerations: data.special_considerations ?? [],
        need_sedations: data.need_sedations ?? [],
      };
      await checkStep('history', fields);
      const draft = draftRef.current;
      return keep(createPatientHistoryAction, {
        ...draft,
        is_urgent: !!data.is_urgent,
        patient: { ...draft.patient, medical_record: { id: 'guest', ...fields } },
      });
    });

    // Applies the step's changes the way the API does: removed services go,
    // new ones are added, a new one's remarks replace the old.
    const saveReason = (data: any) => asRequest(async () => {
      const added = data.new_referral_services ?? [];
      await checkStep('reason', {
        tooth: data.tooth ?? [],
        remarks: data.remarks ?? '',
        new_referral_services: added,
      });
      const draft = draftRef.current;
      const removed = new Set((data.removed_referral_services ?? [])
        .map((service: any) => service.specialties_id));
      let services = (draft.services ?? [])
        .filter((service: any) => !removed.has(service.specialties_id));
      added.forEach((service: any) => {
        const entry = {
          id: service.specialties_id,
          specialties_id: service.specialties_id,
          specialties: service.specialties_name,
          parent_id: service.specialties_parent_id ?? null,
          remarks: service.remarks ?? null,
          details: {
            id: service.specialties_id,
            name: service.specialties_name,
            parent_id: service.specialties_parent_id ?? null,
          },
        };
        services = services.some((s: any) => s.specialties_id === entry.specialties_id)
          ? services.map((s: any) => (s.specialties_id === entry.specialties_id
            ? { ...s, remarks: entry.remarks } : s))
          : [ ...services, entry ];
      });
      return keep(saveReferralReasonAction, {
        ...draft,
        is_urgent: !!data.is_urgent,
        tooth: data.tooth ?? [],
        remarks: data.remarks ?? '',
        services,
      });
    });

    // "Save as draft" keeps the files with the form; Send submits everything.
    const saveAttachments = (data: any) => asRequest(async () => {
      if (data.save_as_draft) {
        return keep(showReferralAction, { ...draftRef.current, attachments: data.files ?? [] });
      }
      return submit(data);
    });

    const catalog = (key: string) => () => asRequest(async () => ({
      data: { data: catalogRef.current?.[key] ?? [] },
    }));

    return {
      createPatientInformationRequest: savePatient,
      updatePatientInformationRequest: savePatient,
      createPatientHistoryRequest: saveHistory,
      updatePatientHistoryRequest: saveHistory,
      saveReferralReasonRequest: saveReason,
      saveReferralAttachmentRequest: saveAttachments,
      getAllergiesRequest: catalog('allergies'),
      getSpecialConsiderationsRequest: catalog('special_considerations'),
      getSedationsRequest: catalog('sedations'),
      getSpecialtiesAndServicesRequest: catalog('specialties_services'),
    };
  }, [ checkStep, keep, submit ]);

  const onSubmitError = useCallback((error: any) => {
    const response = error?.response;
    const code = response?.data?.error_code;

    if (code === 'sender_has_account') {
      setHasAccount(true);
      setOpen('1');
      return null;
    }
    if (code === 'captcha_failed') {
      captchaRef.current.reset();
      setCaptchaError(i18n.errors.captchaFailed);
      return null;
    }
    if (code === 'attachment_invalid') {
      toast.error(i18n.errors.attachment);
      return { files: [ i18n.errors.attachment ] };
    }
    if (response?.status === 422 && response.data?.errors) {
      const { errors }: { errors: Record<string, string[]> } = response.data;
      const keys = Object.keys(errors);

      // The picked dentist left the clinic after the form loaded: back to
      // "Any", with the clinic's current dentists and the reason at the top.
      if (keys.includes('preferred_dentist_id')) {
        const message = LocaleService.parseTranslation(i18n.preferredDentist.unavailable, {
          ':clinic': clinic?.name ?? '',
        });
        pickDentist(null);
        setDentistError(message);
        toast.error(message);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        dispatch(getGuestReferLinkRequest(slug)).$promise
          .then((link: any) => setDentists(link?.data?.data?.dentists ?? []))
          .catch(() => {});
        return null;
      }
      if (keys.includes('consent')) setConsentError(i18n.errors.consent);
      if (keys.includes('submission_key')) submissionKey.current = uuidv4();

      // A step already checked on Next should not fail here; if one does,
      // take the guest back to it.
      const step = [ 'sender', 'patient', 'history', 'reason' ]
        .find((block) => keys.some((key) => key.startsWith(`${block}.`)));
      if (step) {
        toast.error(LocaleService.parseTranslation(i18n.errors.reviewStep, {
          ':number': STEP_OF_BLOCK[step],
        }));
        setOpen(STEP_OF_BLOCK[step]);
        return null;
      }

      const fileErrors = keys.filter((key) => key.startsWith('attachments.'));
      if (fileErrors.length > 0) {
        toast.error(errors[fileErrors[0]][0]);
        return { files: fileErrors.map((key) => errors[key][0]) };
      }
      if (!keys.includes('consent')) handleError(error);
      return null;
    }

    showProblem(error);
    return null;
  }, [ showProblem, pickDentist, clinic, dispatch, slug ]);

  const guest: GuestReferral = useMemo(() => ({
    requests,
    canSubmit: !!sender && consent && captcha.ready,
    submitLabel: i18n.submit,
    onSubmitBlocked: () => {
      if (!sender) {
        toast.error(i18n.errors.sender);
        setOpen('1');
        return;
      }
      if (!consent) setConsentError(i18n.errors.consent);
      if (!captcha.ready) setCaptchaError(i18n.errors.captcha);
    },
    onSubmitted: (result: any) => {
      setSubmitted(result);
      window.scrollTo({ top: 0 });
    },
    onSubmitError,
    submitExtras: (
      <div className="guest-refer-submit-extras">
        <div className="terms-check">
          <Checkbox
            id="guest-refer-consent"
            name="consent"
            type="checkbox"
            checked={consent}
            onInputChangeHandler={() => {
              setConsent(!consent);
              setConsentError('');
            }}
            label={(
              <span className="terms">
                <span className="text-danger">* </span>
                {LocaleService.newParseTranslation(i18n.consent, {
                  terms: (
                    <a href={appConfig.termsAndConditions} className="terms-link" target="_blank" rel="noreferrer">
                      {i18n.termsAndConditions}
                    </a>
                  ),
                  privacyPolicy: (
                    <a href={appConfig.privacyPolicy} className="terms-link" target="_blank" rel="noreferrer">
                      {i18n.privacyPolicy}
                    </a>
                  ),
                })}
              </span>
            )}
            fieldError={consentError}
            formGrpClassName="terms-form-group"
          />
        </div>
        {captcha.widget}
        {captchaError && <div className="guest-refer-captcha-error">{captchaError}</div>}
      </div>
    ),
  }), [ requests, sender, consent, consentError, captcha, captchaError, onSubmitError ]);

  if (loading) return <LoadingBlock withReforalLogo enabled />;

  if (linkProblem || !clinic) {
    const state = linkProblem ?? 'link_invalid';
    return <LinkStateScreen state={state} copy={linkStateCopy(state, clinicContact)} />;
  }

  return (
    <div className="guest-referral-page guest-refer-page">
      <div className="guest-referral-card guest-refer-card">
        {submitted ? (
          <GuestReferralSubmitted result={submitted} />
        ) : (
          <>
            <div className="guest-referral-header">
              <Row className="align-items-center">
                <Col xs={12} sm={7}>
                  <div className="guest-referral-title">{i18n.title}</div>
                </Col>
                <Col xs={12} sm={5} className="guest-refer-urgent">
                  <Checkbox
                    id="is-urgent"
                    name="is-urgent"
                    type="checkbox"
                    formGrpClassName="urgent-group"
                    onInputChangeHandler={() => dispatch(markAsUrgentDispatch(!isUrgent))}
                    label={(
                      <>
                        <span className="urgent-label">{i18nSend.forms.urgent}</span> <UrgentFormFlagIcon />
                      </>
                    )}
                    checked={isUrgent}
                  />
                </Col>
              </Row>
            </div>
            <div className="guest-refer-clinic">
              <div className="guest-refer-clinic-label">{i18n.sendsTo}</div>
              <div className="guest-refer-clinic-card">
                {clinic.image_url ? (
                  <img className="guest-refer-clinic-image" src={clinic.image_url} alt="" />
                ) : (
                  <span className="guest-refer-clinic-image guest-refer-clinic-initials">
                    {getInitials(clinic.name)}
                  </span>
                )}
                <div className="guest-refer-clinic-details">
                  <div className="guest-refer-clinic-name">{clinic.name}</div>
                  {clinic.address && <div className="guest-refer-clinic-line">{clinic.address}</div>}
                  <div className="guest-refer-clinic-contacts">
                    {clinic.phone_number && (
                      <span className="guest-refer-clinic-line">
                        <PhoneIcon /> {formatPhoneNumber(clinic.phone_number)}
                      </span>
                    )}
                    {clinic.email && (
                      <span className="guest-refer-clinic-line">
                        <EmailIcon /> {clinic.email}
                      </span>
                    )}
                  </div>
                </div>
                {dentists.length > 0 && (
                  <GuestPreferredDentist
                    clinicName={clinic.name}
                    dentists={dentists}
                    value={dentistId}
                    onChange={pickDentist}
                    error={dentistError}
                  />
                )}
              </div>
            </div>
            <div className="send-referral-order-page-container guest-refer-steps">
              <GuestReferralProvider value={guest}>
                <GuestSenderForm
                  slug={slug}
                  clinic={clinic}
                  open={open}
                  toggle={toggle}
                  sender={sender}
                  onSaved={(value) => {
                    senderRef.current = value;
                    setSender(value);
                  }}
                  hasAccount={hasAccount}
                  setHasAccount={setHasAccount}
                  onError={showProblem}
                />
                <PatientInformationForm
                  toggle={toggle}
                  open={open}
                  formType="referral"
                  loadingDetails={false}
                />
                <PatientHistoryForm
                  toggle={toggle}
                  open={open}
                  loadingDetails={false}
                />
                <ReferralReasonForm
                  toggle={toggle}
                  open={open}
                  loadingDetails={false}
                />
                <AttachmentForm
                  toggle={toggle}
                  open={open}
                />
              </GuestReferralProvider>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GuestReferPage;
